import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { sha256DeBase64, base64DeDataUrl } from '../almacen/cripto.js';

/**
 * Captura de firma grafométrica (HU-03). Registra los TRAZOS VECTORIALES
 * (puntos por trazo), la duración y la cantidad de trazos, además del PNG y su
 * hash SHA-256 — porque "la imagen sola es evidencia débil" (Anexo A). El
 * servidor recalcula el hash; el sello temporal lo pone el servidor.
 */

export interface CapturaFirma {
  imagenPngBase64: string;
  hashSha256: string;
  trazos: { formato: string; datos: string };
  duracionMs: number;
  cantidadTrazos: number;
}

export interface FirmaHandle {
  estaVacia: () => boolean;
  limpiar: () => void;
  capturar: () => Promise<CapturaFirma | null>;
}

type Punto = { x: number; y: number; t: number };

export const Firma = forwardRef<FirmaHandle, { alto?: number }>(function Firma({ alto = 150 }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trazos = useRef<Punto[][]>([]);
  const trazoActual = useRef<Punto[] | null>(null);
  const inicio = useRef<number>(0);
  const [vacia, setVacia] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ratio = window.devicePixelRatio || 1;
    const ancho = canvas.clientWidth;
    canvas.width = ancho * ratio;
    canvas.height = alto * ratio;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a18';
  }, [alto]);

  function posicion(e: PointerEvent): Punto {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, t: performance.now() };
  }

  function onDown(e: React.PointerEvent) {
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    if (trazos.current.length === 0) inicio.current = performance.now();
    trazoActual.current = [posicion(e.nativeEvent)];
    setVacia(false);
  }
  function onMove(e: React.PointerEvent) {
    if (!trazoActual.current) return;
    const p = posicion(e.nativeEvent);
    trazoActual.current.push(p);
    const ctx = canvasRef.current!.getContext('2d')!;
    const ts = trazoActual.current;
    const a = ts[ts.length - 2];
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }
  function onUp() {
    if (trazoActual.current && trazoActual.current.length > 0) trazos.current.push(trazoActual.current);
    trazoActual.current = null;
  }

  function limpiar() {
    const canvas = canvasRef.current!;
    canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    trazos.current = [];
    trazoActual.current = null;
    setVacia(true);
  }

  useImperativeHandle(ref, () => ({
    estaVacia: () => vacia,
    limpiar,
    capturar: async () => {
      if (vacia) return null;
      const dataUrl = canvasRef.current!.toDataURL('image/png');
      const imagenPngBase64 = base64DeDataUrl(dataUrl);
      const hashSha256 = await sha256DeBase64(imagenPngBase64);
      return {
        imagenPngBase64,
        hashSha256,
        trazos: {
          formato: 'ausentia-trazos/v1',
          datos: JSON.stringify(
            trazos.current.map((t) => t.map((p) => [Math.round(p.x), Math.round(p.y), Math.round(p.t)])),
          ),
        },
        duracionMs: Math.round(performance.now() - inicio.current),
        cantidadTrazos: trazos.current.length,
      };
    },
  }));

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="lienzo-firma"
        style={{ height: alto }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <button type="button" className="fantasma" onClick={limpiar}>
          ✏️ Rehacer
        </button>
        <span className="mini">{vacia ? 'Firme dentro del recuadro' : `${trazos.current.length} trazo(s)`}</span>
      </div>
    </div>
  );
});
